import { ReactNode, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Header } from './Header';
import { Footer } from './Footer';
import { FloatingNotificationCenter } from '@/components/FloatingNotificationCenter';
import { QuickActionMinibar } from '@/components/home/QuickActionMinibar';
import { InstallPrompt } from '@/components/pwa/InstallPrompt';
import { NetworkStatus } from '@/components/pwa/NetworkStatus';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { OfflinePage } from '@/components/pwa/OfflinePage';
import { useActivityTracker } from '@/hooks/useActivityTracker';

interface MainLayoutProps {
  children: ReactNode;
}

export function MainLayout({ children }: MainLayoutProps) {
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [isCheckingMaintenance, setIsCheckingMaintenance] = useState(true);
  const { isAdmin, isEditor, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isOnline } = useNetworkStatus();
  
  // Track user activity
  useActivityTracker();

  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const { data } = await supabase
          .from('site_settings')
          .select('value')
          .eq('key', 'maintenance_mode')
          .single();

        const maintenanceEnabled = data?.value === true;
        setIsMaintenanceMode(maintenanceEnabled);

        // If maintenance is enabled and user is not admin/editor, redirect to maintenance page
        if (maintenanceEnabled && !authLoading && !isAdmin && !isEditor && location.pathname !== '/maintenance') {
          navigate('/maintenance', { replace: true });
        }
      } catch (error) {
        console.error('Error checking maintenance mode:', error);
      } finally {
        setIsCheckingMaintenance(false);
      }
    };

    // Don't check maintenance on the maintenance page itself
    if (location.pathname !== '/maintenance') {
      checkMaintenance();
    } else {
      setIsCheckingMaintenance(false);
    }
  }, [authLoading, isAdmin, isEditor, navigate, location.pathname]);

  // Show nothing while checking maintenance (prevents flash)
  if (isCheckingMaintenance && location.pathname !== '/maintenance') {
    return null;
  }

  // Show offline page when not connected
  if (!isOnline) {
    return <OfflinePage />;
  }

  return (
    <div className="min-h-screen min-h-[100dvh] flex flex-col overflow-x-hidden">
      {/* Network status indicator */}
      <NetworkStatus />
      
      <Header />
      <main className="flex-1 pt-11 md:pt-16 mobile-safe-bottom overflow-x-hidden">
        {children}
      </main>
      <Footer />
      <FloatingNotificationCenter />
      <QuickActionMinibar />
      <InstallPrompt />
    </div>
  );
}
